import React, { useEffect, useState } from 'react';
import { safeFetch } from '../../lib/api';

/**
 * "Privacy & your data" panel for a signed-in user (Slice D D-04):
 * submit a data-rights request, see your requests and their 30-day SLA,
 * and download a copy of your own data. Rendered inside account settings.
 */
export default function PrivacySettingsPanel() {
  const [type, setType] = useState('access');
  const [message, setMessage] = useState('');
  const [requests, setRequests] = useState<any[]>([]);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const data = await safeFetch('/api/legal/privacy-requests/mine');
      setRequests(Array.isArray(data.requests) ? data.requests : []);
    } catch { /* panel still renders the form */ }
  };
  useEffect(() => { void load(); }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(''); setNotice('');
    try {
      const data = await safeFetch('/api/legal/privacy-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, message }),
      });
      setNotice(`Request received. We respond within 30 days${data?.slaDueAt ? ` (by ${new Date(data.slaDueAt).toLocaleDateString()})` : ''}.`);
      setMessage('');
      await load();
    } catch (err: any) {
      setError(err?.message || 'Could not submit the request. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const downloadExport = async () => {
    setError('');
    try {
      const res = await fetch('/api/legal/my-export', {
        headers: { ...(await authHeaders()) },
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'my-data-export.json';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Could not build your export right now. Please submit an access request instead.');
    }
  };

  const inputCls = 'w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white';
  return (
    <section aria-label="Privacy and your data" className="space-y-4">
      <div>
        <h3 className="text-sm font-bold text-slate-800">Privacy &amp; your data</h3>
        <p className="text-xs text-slate-500 mt-1">
          Request a copy of your information, a correction, or deletion. Requests are answered within 30 days.
        </p>
      </div>
      <form onSubmit={submit} className="space-y-3">
        <label className="block text-xs font-semibold text-slate-600">
          Request type
          <select className={inputCls} value={type} onChange={e => setType(e.target.value)}>
            <option value="access">Access — show me my information</option>
            <option value="portability">Portability — send me a copy</option>
            <option value="correction">Correction — fix something wrong</option>
            <option value="deletion">Deletion — delete my information</option>
            <option value="withdraw_consent">Withdraw consent</option>
            <option value="limit_use">Limit use of sensitive information</option>
          </select>
        </label>
        <label className="block text-xs font-semibold text-slate-600">
          Details (optional)
          <textarea className={inputCls} rows={3} value={message} onChange={e => setMessage(e.target.value)} />
        </label>
        {notice && <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2" role="status">{notice}</p>}
        {error && <p className="text-xs text-rose-600" role="alert">{error}</p>}
        <div className="flex flex-wrap gap-2">
          <button type="submit" disabled={busy} className="px-4 py-2 btn-primary rounded-xl text-xs font-semibold cursor-pointer disabled:opacity-50">
            Submit request
          </button>
          <button type="button" onClick={downloadExport} className="px-4 py-2 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 cursor-pointer hover:bg-slate-50">
            Download my data (JSON)
          </button>
        </div>
      </form>
      {requests.length > 0 && (
        <ul className="divide-y divide-slate-100 border border-slate-100 rounded-xl">
          {requests.map(r => (
            <li key={r.id} className="px-3 py-2 text-xs flex items-center justify-between gap-3">
              <span className="font-semibold text-slate-700 capitalize">{String(r.type).replace('_', ' ')}</span>
              <span className="text-slate-500">{r.status}</span>
              <span className="text-slate-400">due {r.slaDueAt ? new Date(r.slaDueAt).toLocaleDateString() : ''}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

async function authHeaders(): Promise<Record<string, string>> {
  try {
    const { auth } = await import('../../lib/firebase');
    const token = auth?.currentUser ? await auth.currentUser.getIdToken().catch(() => null) : null;
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}
